import { Dropdown } from '@design/components/dropdown/Dropdown';
import { DropdownListItem } from '@design/components/dropdown/dropdown-list-item/DropdownListItem';
import { Select } from '@design/components/select/Select';
import {
  useRemoteStorage,
  WHATS_NEW_PERIOD,
} from '@terminal/hooks/use-remote-storage';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_WHATS_NEW_PERIOD,
  InsightsPeriod,
  isInsightsPeriod,
} from '../../types';
import styles from './PeriodChanger.module.scss';

type PeriodOption = {
  label: string;
  value: InsightsPeriod;
};

export const PeriodChanger = () => {
  const { t } = useTranslation(['whatsNew']);
  const [isOpen, setIsOpen] = useState(false);
  const { items, storeItems } = useRemoteStorage(state => ({
    storeItems: state.storeItems,
    items: state.items,
  }));
  const storedPeriod = items?.[WHATS_NEW_PERIOD];
  const period = isInsightsPeriod(storedPeriod)
    ? storedPeriod
    : DEFAULT_WHATS_NEW_PERIOD;

  const periodOptions: PeriodOption[] = useMemo(
    () => [
      {
        label: t('whatsNew:periodDays', {
          count: 1,
        }),
        value: { duration: 1, unit: 'day' },
      },
      {
        label: t('whatsNew:periodDays', {
          count: 3,
        }),
        value: { duration: 3, unit: 'day' },
      },
      {
        label: t('whatsNew:periodWeek'),
        value: { duration: 7, unit: 'day' },
      },
    ],
    [t]
  );
  const foundIndex = periodOptions.findIndex(
    option =>
      option.value.unit === period.unit &&
      option.value.duration === period.duration
  );
  const selectedIndex = Math.max(foundIndex, 0);

  useEffect(() => {
    if (foundIndex === -1) {
      onSelectOption(selectedIndex);
    }
  }, [foundIndex, selectedIndex]);

  const onSelectOption = (index: number) => {
    setIsOpen(false);
    storeItems({ [WHATS_NEW_PERIOD]: periodOptions[index].value });
  };

  return (
    <Dropdown<PeriodOption>
      className={styles.styledDropdown}
      onClick={() => setIsOpen(!isOpen)}
      activeItemIdx={selectedIndex}
      onOutsideClick={() => setIsOpen(false)}
      dropdownItems={periodOptions}
      selectItem={(_, _trigger, idx) => {
        onSelectOption(idx);
      }}
      shouldShowItems={false}
      renderDropdownItem={(item, isActive, isFocused) => (
        <DropdownListItem
          label={item.label}
          isActive={isActive}
          isFocused={isFocused}
        />
      )}
    >
      <div className={styles.selectWrapper}>
        <Select
          isActive={isOpen}
          inputProps={{
            value: periodOptions[selectedIndex].label,
            variant: 'outline',
          }}
        />
      </div>
    </Dropdown>
  );
};
